// frontend-firstarabic/src/components/video/KeynoteViewer.js
// Affiche une présentation Keynote en plein écran dans la session vidéo
// Sync prof/élève via Agora :
//  - Prof : capture les inputs dans l'iframe et les envoie via Agora
//  - Élève : reçoit les inputs depuis Agora et les rejoue dans l'iframe

import React, { useEffect, useRef, useState } from 'react';

const KeynoteViewer = ({
  iframeUrl,
  nom,
  lettre,
  onClose,
  userType,
  onProfInput,       // callback : (input) => void — appelé côté prof à chaque action capturée
  syncInputFromProf, // input à rejouer côté élève (change à chaque action reçue)
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const iframeRef = useRef(null);

  // Empêcher le scroll de la page en arrière-plan
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Touche Esc pour fermer (uniquement côté prof)
  useEffect(() => {
    if (userType !== 'prof') return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [userType, onClose]);

  // Envoyer le rôle à l'iframe après chargement
  const onIframeLoad = () => {
    setIsLoading(false);
    const role = userType === 'prof' ? 'prof' : 'eleve';
    try {
      iframeRef.current?.contentWindow?.postMessage({
        type: 'keynote_set_role',
        role: role,
      }, '*');
      console.log('[KeynoteViewer] Rôle envoyé à l\'iframe:', role);
    } catch (e) {
      console.warn('[KeynoteViewer] Impossible d\'envoyer le rôle:', e);
    }
  };

  // Côté prof : recevoir les inputs capturés depuis l'iframe et les transmettre
  useEffect(() => {
    if (userType !== 'prof' || !onProfInput) return;

    const handleMessage = (e) => {
      const data = e.data;
      if (!data || typeof data !== 'object') return;
      if (data.type === 'keynote_input_captured') {
        onProfInput(data.input);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [userType, onProfInput]);

  // Côté élève : transmettre les inputs reçus à l'iframe
  useEffect(() => {
    if (userType === 'prof') return;
    if (!syncInputFromProf) return;

    // syncInputFromProf est un wrapper { input, ts } pour forcer le re-render
    // On extrait le vrai input pour le rejouer
    const actualInput = syncInputFromProf.input || syncInputFromProf;

    try {
      iframeRef.current?.contentWindow?.postMessage({
        type: 'keynote_replay_input',
        input: actualInput,
      }, '*');
      console.log('[KeynoteViewer] Input rejoué côté élève:', actualInput);
    } catch (e) {
      console.warn('[KeynoteViewer] Impossible de rejouer input:', e);
    }
  }, [syncInputFromProf, userType]);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 74,
      background: '#000',
      zIndex: 1000,
    }}>
      {/* Header titre + bouton fermer */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        padding: '10px 16px',
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)',
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pointerEvents: 'none',
      }}>
        <div style={{
          color: 'white',
          fontSize: 14,
          fontWeight: 500,
          textShadow: '0 1px 3px rgba(0,0,0,0.8)',
          pointerEvents: 'auto',
        }}>
          📊 {nom}{lettre ? ` — ${lettre}` : ''}
        </div>

        {userType === 'prof' && (
          <button
            onClick={onClose}
            style={{
              background: 'rgba(0,0,0,0.6)',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '50%',
              width: 36,
              height: 36,
              fontSize: 18,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'auto',
            }}
            title="Fermer la présentation (Esc)"
          >
            ×
          </button>
        )}
      </div>

      {/* Loader */}
      {isLoading && (
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: 14,
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            border: '3px solid rgba(255,255,255,0.2)',
            borderTopColor: 'white',
            animation: 'spin 1s linear infinite',
            marginRight: 12,
          }} />
          Chargement de la présentation…
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Iframe */}
      <iframe
        ref={iframeRef}
        src={iframeUrl}
        title={nom}
        onLoad={onIframeLoad}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          background: '#000',
        }}
        allow="autoplay; fullscreen"
      />
    </div>
  );
};

export default KeynoteViewer;
